import React from "react";
import dayjs from "dayjs";
import {
    Box, Badge, Spinner, Table, Tbody, Td, Th, Thead, Tr, TableContainer, Text, useColorModeValue,
} from "@chakra-ui/react";
import {
    TIER_LABELS,
    TIER_COLORS,
    STATUS_LABELS,
    STATUS_COLORS,
    PROVIDER_LABELS,
    PROVIDER_COLORS,
    DEFAULT_CHIP_COLOR,
} from "../../components/Main/auditorAndArtiste/subscriptions/subscriptionsConstants";
import OwnerCell from "./OwnerCell";

/**
 * Renders a colored badge from a {bg,color,border} scheme.
 * @param {Object} props - { scheme, label }
 * @returns {React.ReactElement}
 */
function SchemeBadge({ scheme, label }) {
    return (
        <Badge bg={scheme.bg} color={scheme.color} borderColor={scheme.border} borderWidth="1px"
            borderRadius="8px" px="8px" py="2px" fontSize="0.7rem" textTransform="none">{label}</Badge>
    );
}

/**
 * Formats a date as DD/MM/YYYY or "—" when missing/invalid.
 * @param {string} value - ISO date string
 * @returns {string}
 */
function formatDate(value) {
    if (!value) return "—";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "—";
}

/**
 * Formats an amount with its currency code, or "—" when missing.
 * @param {number|null|undefined} amount - Numeric amount
 * @param {string} currency - Currency code
 * @returns {string}
 */
function formatAmount(amount, currency) {
    if (amount === null || amount === undefined) return "—";
    return `${amount} ${(currency || "").toUpperCase()}`.trim();
}

/**
 * Renders a single subscription row in the global list.
 * @param {Object} props
 * @param {Object} props.sub - Subscription object
 * @param {Function} props.onSelect - Row click handler
 * @returns {React.ReactElement}
 */
function ListRow({ sub, onSelect }) {
    const hoverBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const period = `${formatDate(sub.start_date)} → ${formatDate(sub.end_date)}`;
    return (
        <Tr cursor="pointer" _hover={{ bg: hoverBg }} onClick={() => onSelect?.(sub.id)}>
            <Td><OwnerCell ownerType={sub.owner_type} ownerId={sub.owner_id} /></Td>
            <Td><SchemeBadge scheme={TIER_COLORS[sub.subscription_type] || DEFAULT_CHIP_COLOR} label={TIER_LABELS[sub.subscription_type] || sub.subscription_type || "—"} /></Td>
            <Td><SchemeBadge scheme={STATUS_COLORS[sub.status] || DEFAULT_CHIP_COLOR} label={STATUS_LABELS[sub.status] || sub.status || "—"} /></Td>
            <Td><SchemeBadge scheme={PROVIDER_COLORS[sub.payment_provider] || DEFAULT_CHIP_COLOR} label={PROVIDER_LABELS[sub.payment_provider] || sub.payment_provider || "—"} /></Td>
            <Td fontSize="0.8rem">{period}</Td>
            <Td fontSize="0.8rem">{formatAmount(sub.amount, sub.currency)}</Td>
            <Td fontSize="0.8rem">{sub.auto_renew ? "Oui" : "Non"}</Td>
        </Tr>
    );
}

/**
 * Centered state box for loading / error / empty.
 * @param {Object} props - { children, color }
 * @returns {React.ReactElement}
 */
function StateBox({ children }) {
    return <Box textAlign="center" py="40px">{children}</Box>;
}

/**
 * Picks the body component based on loading/error/empty/data state.
 * @param {Object} args - State and items
 * @returns {React.ReactElement}
 */
function buildBody({ items, loading, error, onSelect, headColor }) {
    if (loading && (!items || items.length === 0)) return <StateBox><Spinner color="brand.500" /></StateBox>;
    if (error && (!items || items.length === 0)) {
        return <StateBox><Text color="red.400" fontSize="0.85rem">{typeof error === "string" ? error : "Erreur lors du chargement"}</Text></StateBox>;
    }
    if (!items || items.length === 0) return <StateBox><Text color="secondaryGray.600" fontSize="0.85rem">Aucun abonnement</Text></StateBox>;
    return (
        <TableContainer borderRadius="12px">
            <Table size="sm">
                <Thead>
                    <Tr>
                        <Th color={headColor}>Propriétaire</Th><Th color={headColor}>Type</Th><Th color={headColor}>Statut</Th>
                        <Th color={headColor}>Paiement</Th><Th color={headColor}>Période</Th><Th color={headColor}>Montant</Th>
                        <Th color={headColor}>Auto-renouv.</Th>
                    </Tr>
                </Thead>
                <Tbody>{items.map((sub) => <ListRow key={sub.id} sub={sub} onSelect={onSelect} />)}</Tbody>
            </Table>
        </TableContainer>
    );
}

/**
 * Global subscriptions table with Owner column. Handles loading / error / empty internally.
 * @param {Object} props
 * @param {Array} props.items - Subscription list
 * @param {boolean} props.loading - Loading flag
 * @param {string|null} props.error - Error message
 * @param {Function} props.onSelect - Row click handler
 * @returns {React.ReactElement}
 */
function SubscriptionsListTable({ items, loading, error, onSelect }) {
    const headColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return buildBody({ items, loading, error, onSelect, headColor });
}

export default SubscriptionsListTable;
